/**
 * Date utilities that are timezone-aware.
 *
 * Rule of thumb:
 *  - "date labels" (due dates, meeting dates, created-at dates) → YYYY-MM-DD strings,
 *    derived from the workspace or browser local calendar (never UTC)
 *  - "moments in time" (audit logs, notifications, comments) → UTC ISO strings,
 *    displayed via toLocaleString() so the viewer's browser localises them automatically
 */

/** Returns today as YYYY-MM-DD in the browser's local calendar. */
export function localDateStr(): string {
  const d = new Date();
  return _ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Returns today + |days| offset as YYYY-MM-DD in the browser's local calendar. */
export function localDateStrOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return _ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Extracts the local calendar date from a Date object as YYYY-MM-DD. */
export function dateToLocalStr(d: Date): string {
  return _ymd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Returns today as YYYY-MM-DD in the given IANA timezone (e.g. "America/New_York"). */
export function todayInTz(tz: string): string {
  // en-CA locale formats as YYYY-MM-DD natively
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());
}

/** Returns today + |days| offset as YYYY-MM-DD in the given IANA timezone. */
export function dateOffsetInTz(days: number, tz: string): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d);
}

/**
 * Parses a YYYY-MM-DD string as local midnight.
 * Use this instead of `new Date(s)` — without a time component the JS Date
 * parser treats the string as UTC midnight, which shifts the displayed date
 * for users west of UTC.
 */
export function parseDateStr(s: string): Date {
  return new Date(s + 'T00:00:00');
}

export function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/**
 * Formats a UTC ISO timestamp (timestamptz) as a localised date+time string
 * in the viewer's browser timezone.  Suitable for audit logs, notifications,
 * comments — anything that records a moment in time.
 */
export function formatLocalTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/** Formats a UTC ISO timestamp as a localised date-only string with the full month name. */
export function formatLocalDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

/**
 * Formats a UTC ISO timestamp as "5m ago" / "3h ago" / "2d ago" for up to 7
 * days, then falls back to a short absolute date ("Jan 5"). Used for the
 * "sent <RELATIVE TIME> ago" / "sent <DATE>" pattern on invitations.
 */
export function formatShortRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays >= 7) {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (diffDays >= 1) return `${diffDays}d ago`;
  if (diffHours >= 1) return `${diffHours}h ago`;
  if (diffMin >= 1) return `${diffMin}m ago`;
  return 'just now';
}

export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const count = (value: number, unit: string) => `${value} ${unit}${value === 1 ? '' : 's'} ago`;
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (days >= 7) {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (days >= 1) return count(days, 'day');
  if (hours >= 1) return count(hours, 'hour');
  if (minutes >= 1) return count(minutes, 'minute');
  return 'just now';
}

/**
 * Formats a past YYYY-MM-DD calendar date (e.g. `createdAt`) as "today" / "N days ago" for up
 * to 30 days, then falls back to a short absolute date ("Mar 5"). For "Date Added"-style
 * provenance labels where the exact day stops mattering once it's been a while — a longer
 * window than `formatShortRelativeTime`'s 7 days since this is calendar-day, not live-clock,
 * precision to begin with.
 */
export function formatDaysAgo(dateStr: string): string {
  const date = parseDateStr(dateStr);
  const today = parseDateStr(localDateStr());
  const diffDays = Math.round((today.getTime() - date.getTime()) / 86400000);

  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays <= 30) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * A due date as a reader thinks of it: relative while it is near, absolute once
 * it is not. "today", "tomorrow", "in 3 days", "2 days ago", then "Sep 4".
 *
 * Both directions, unlike `formatDaysAgo` — a deadline is usually ahead — and a
 * 7-day window, because past that the day of the week stops being the useful
 * fact and the date itself starts being one.
 *
 * `todayStr` is required rather than defaulted to the browser's day: a deadline
 * belongs to the workspace that set it, and two workspaces in different zones
 * disagree about whether the same date is still today.
 */
export function formatDueDay(dateStr: string, todayStr: string): string {
  const date = parseDateStr(dateStr);
  const today = parseDateStr(todayStr);
  const days = Math.round((date.getTime() - today.getTime()) / 86400000);

  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 1 && days <= 7) return `in ${days} days`;
  if (days < -1 && days >= -7) return `${Math.abs(days)} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Converts a UTC ISO string to 'YYYY-MM-DDTHH:MM' for initialising a
 * <input type="datetime-local">, which always operates in browser-local wall-clock time.
 */
export function toDatetimeLocal(utcIso: string): string {
  const d = new Date(utcIso);
  // getTimezoneOffset() returns (UTC − local) in minutes; subtracting shifts UTC → local wall clock
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/**
 * Returns the UTC ISO string for a wall-clock date+time in a specific IANA timezone.
 * Use this when a time was configured in workspace timezone and must be stored as UTC.
 */
export function wallClockInTzToUTC(dateStr: string, timeHHMM: string, tz: string): string {
  const [h, m] = timeHHMM.split(':').map(Number);
  // Treat the wall-clock time as UTC to get a starting approximation
  const naive = new Date(`${dateStr}T${timeHHMM}:00Z`);
  // See what the naive UTC instant shows in the target timezone
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(naive);
  const shownH = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0') % 24;
  const shownM = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0');
  // Shift naive by the difference between desired wall-clock time and what it currently shows
  const diffMs = ((h - shownH) * 60 + (m - shownM)) * 60000;
  return new Date(naive.getTime() + diffMs).toISOString();
}

/** Returns a short timezone abbreviation (e.g. "PDT", "EST") for a given IANA timezone at an instant. */
export function tzAbbr(tz: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(at);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

/** Formats the current time in a given IANA timezone as "Sat 1:00 pm" style. */
export function currentTimeInTz(tz: string, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
    }).formatToParts(now);
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
    return `${get('weekday')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toLowerCase()}`;
  } catch {
    return '';
  }
}

/** Formats a 24h "HH:MM" string as "H:MM AM/PM" (e.g. "13:00" → "1:00 PM"). */
export function formatHHMM(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** Returns "Wed 1:00 PM" — short weekday + 12h time — for a timezone at a given instant. */
export function formatDayTimeInTz(tz: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(at);
    const weekday = parts.find(p => p.type === 'weekday')?.value ?? '';
    const h = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0') % 24;
    const m = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0');
    return `${weekday} ${formatHHMM(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)}`;
  } catch {
    return '';
  }
}

// --- internal ---
function _ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
